<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWizardConfigStore } from 'dashboard/store/wizard/preferences';
import ModelDropdown from './ModelDropdown.vue';

const props = defineProps({
  featureKey: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    required: true,
  },
  isAllowed: {
    type: Boolean,
    required: true,
  },
  allowedProviders: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['change']);
const { t } = useI18n();
const wizardConfigStore = useWizardConfigStore();
const featureConfig = computed(() =>
  wizardConfigStore.getFeatureConfig(props.featureKey)
);

const featureModels = computed(() =>
  wizardConfigStore.getModelsForFeature(props.featureKey)
);

const defaultModelDetails = computed(() => {
  const fallbackModelId =
    featureConfig.value?.selected_source === 'installation'
      ? featureConfig.value?.installation_default
      : featureConfig.value?.feature_default;

  if (!fallbackModelId) return null;

  return (
    featureModels.value.find(model => model.id === fallbackModelId) || null
  );
});

const defaultStateMessage = computed(() => {
  if (!defaultModelDetails.value) return '';

  if (featureConfig.value?.selected_source === 'installation') {
    return t('WIZARD_SETTINGS.MODEL_CONFIG.USING_INSTALLATION_DEFAULT', {
      model: defaultModelDetails.value.display_name,
    });
  }

  if (featureConfig.value?.selected_source === 'feature_default') {
    return t('WIZARD_SETTINGS.MODEL_CONFIG.USING_FEATURE_DEFAULT', {
      model: defaultModelDetails.value.display_name,
    });
  }

  return '';
});

const handleModelChange = ({ feature, model }) => {
  emit('change', { feature, model });
};
</script>

<template>
  <div
    class="flex items-center justify-between gap-4 p-4 rounded-xl border border-n-weak bg-n-solid-1"
    :class="{ 'opacity-60 pointer-events-none relative': !isAllowed }"
  >
    <div class="flex-1 min-w-0">
      <h4 class="text-sm font-medium text-n-slate-12">
        {{ title }}
      </h4>
      <p class="text-sm text-n-slate-11 mt-0.5">{{ description }}</p>
      <p v-if="defaultStateMessage" class="text-xs text-n-slate-10 mt-2">
        {{ defaultStateMessage }}
      </p>
    </div>
    <ModelDropdown
      v-if="isAllowed"
      :feature-key="featureKey"
      :allowed-providers="allowedProviders"
      @change="handleModelChange"
    />
  </div>
</template>
